import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  MessageCircle,
  Edit2,
  Trash2,
  Send,
  X,
  Check,
  MoreVertical,
  Pin,
  Users,
  Hash,
  Smile,
  Paperclip,
  Moon,
  Sun,
  Settings,
  Search,
  Filter,
  Bell,
  BellOff,
  Star,
  StarOff,
  Code,
  Link,
  Image,
  FileText,
  ChevronDown,
  ChevronUp,
  Globe,
  Lock,
  Shield,
  Crown,
  Zap,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  ThumbsUp,
  Reply,
  Share,
  Bookmark,
  Flag,
  Eye,
  EyeOff,
  Megaphone,
  HelpCircle,
  Video,
  File,
  Download,
  Loader2,
} from 'lucide-react';
import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  getDocs,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { auth } from '../../database/firebase';
import { storage } from '../../database/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
const useCurrentUser = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async firebaseUser => {
      if (firebaseUser) {
        try {
          const adminDoc = await getDoc(doc(firestore, 'admin', firebaseUser.uid));
          if (adminDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'admin',
              ...adminDoc.data(),
            });
            return;
          }
          const superAdminDoc = await getDoc(doc(firestore, 's_admin', firebaseUser.uid));
          if (superAdminDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'superAdmin',
              ...superAdminDoc.data(),
            });
            return;
          }
          const alumniDoc = await getDoc(doc(firestore, 'alumni', firebaseUser.uid));
          if (alumniDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'alumni',
              ...alumniDoc.data(),
            });
            return;
          }
          const moderatorDoc = await getDoc(doc(firestore, 'moderator', firebaseUser.uid));
          if (moderatorDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'moderator',
              ...moderatorDoc.data(),
            });
            return;
          }
          const clientDoc = await getDoc(doc(firestore, 'clients', firebaseUser.uid));
          if (clientDoc.exists()) {
            setUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              role: 'client',
              ...clientDoc.data(),
            });
            return;
          }
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            role: 'user',
          });
        } catch (error) {
          toast.error('Error fetching user data');
          setUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            role: 'user',
          });
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  return user;
};

const Message = ({
  message,
  currentUser,
  onLike,
  onReply,
  onEdit,
  onDelete,
  onUpdateReply,
  onDeleteReply,
  onReplyLike,
  onNestedReply,
  replyingTo,
  setReplyingTo,
  replyText,
  setReplyText,
  editingMessage,
  setEditingMessage,
  editText,
  setEditText,
  editingReply,
  setEditingReply,
  editReplyText,
  setEditReplyText,
  nestedReplyTo,
  setNestedReplyTo,
  nestedReplyText,
  setNestedReplyText,
  expandedReplies,
  setExpandedReplies,
  isDarkMode = false,
  openOptionsMenu,
  setOpenOptionsMenu,
  openReplyOptionsMenu,
  setOpenReplyOptionsMenu,
}) => {
  const isOwnMessage = currentUser && currentUser.id === message.authorId;
  const canEditDelete =
    currentUser &&
    (currentUser.id === message.authorId ||
      currentUser.role === 'admin' ||
      currentUser.role === 'moderator' ||
      currentUser.role === 'superAdmin');

  const getRoleColor = role => {
    const colors = {
      admin: 'bg-red-50 text-black border-gray-900',
      moderator: 'bg-blue-50 text-black border-gray-900',
      alumni: 'bg-green-50 text-black border-gray-900',
      client: 'bg-purple-50 text-black border-gray-900',
      user: 'bg-gray-50 text-black border-gray-900',
    };
    return colors[role] || colors.user;
  };

  const formatTime = timestamp => {
    if (!timestamp?.toDate) return 'now';
    const date = timestamp.toDate();
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group mb-4`}
    >
      <div
        className={`flex max-w-[85%] md:max-w-[70%] ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-start gap-3`}
      >
        <div className="flex-shrink-0">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white shadow-lg ${
              isOwnMessage ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            {message.authorName
              ?.split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase() || 'U'}
          </div>
        </div>

        <div className={`flex-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
          <div
            className={`mb-2 flex items-center gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
          >
            <span
              className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              {message.authorName}
            </span>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${getRoleColor(message.authorRole)}`}
            >
              {message.authorRole}
            </span>
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatTime(message.createdAt)}
            </span>
            {message.isEdited && (
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                (edited)
              </span>
            )}
          </div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className={`inline-block rounded-2xl px-4 py-3 shadow-sm ${
              isOwnMessage
                ? 'rounded-br-md bg-blue-600 text-white'
                : isDarkMode
                  ? 'rounded-bl-md border border-gray-700 bg-gray-800 text-white'
                  : 'rounded-bl-md border border-gray-200 bg-white text-gray-900'
            }`}
          >
            {editingMessage === message.id ? (
              <div className="flex items-center gap-2">
                <input
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  className={`flex-1 border-none bg-transparent text-sm outline-none ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                  onKeyDown={e => {
                    if (e.key === 'Enter') onEdit(message.id);
                    if (e.key === 'Escape') {
                      setEditingMessage(null);
                      setEditText('');
                    }
                  }}
                  autoFocus
                />
                <button
                  onClick={() => onEdit(message.id)}
                  className="rounded p-1 hover:bg-white/20"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={() => {
                    setEditingMessage(null);
                    setEditText('');
                  }}
                  className="rounded p-1 hover:bg-white/20"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                {message.text && (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.text}</p>
                )}
                {message.files && message.files.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {message.files.map((file, idx) => {
                      const isImage = file.type?.startsWith('image/');
                      const isVideo = file.type?.startsWith('video/');
                      const fileSize = file.size
                        ? (file.size / 1024 / 1024).toFixed(2) + ' MB'
                        : '';

                      return (
                        <div key={idx} className="overflow-hidden rounded-lg">
                          {isImage ? (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={file.url}
                                alt={file.name}
                                className="max-h-96 max-w-full cursor-pointer rounded-lg transition-opacity hover:opacity-90"
                              />
                            </a>
                          ) : isVideo ? (
                            <video
                              src={file.url}
                              controls
                              className="max-h-96 max-w-full rounded-lg"
                            >
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-3 rounded-lg p-3 transition-colors ${
                                isOwnMessage
                                  ? 'bg-white/20 hover:bg-white/30'
                                  : isDarkMode
                                    ? 'bg-gray-700 hover:bg-gray-600'
                                    : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                            >
                              <FileText
                                size={24}
                                className={
                                  isOwnMessage
                                    ? 'text-white'
                                    : isDarkMode
                                      ? 'text-gray-300'
                                      : 'text-gray-600'
                                }
                              />
                              <div className="min-w-0 flex-1">
                                <p
                                  className={`truncate text-sm font-medium ${
                                    isOwnMessage
                                      ? 'text-white'
                                      : isDarkMode
                                        ? 'text-white'
                                        : 'text-gray-900'
                                  }`}
                                >
                                  {file.name}
                                </p>
                                {fileSize && (
                                  <p
                                    className={`text-xs ${
                                      isOwnMessage
                                        ? 'text-white/70'
                                        : isDarkMode
                                          ? 'text-gray-400'
                                          : 'text-gray-500'
                                    }`}
                                  >
                                    {fileSize}
                                  </p>
                                )}
                              </div>
                              <Download
                                size={18}
                                className={
                                  isOwnMessage
                                    ? 'text-white'
                                    : isDarkMode
                                      ? 'text-gray-300'
                                      : 'text-gray-600'
                                }
                              />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </motion.div>

          <div
            className={`mt-3 flex items-center gap-4 ${isOwnMessage ? 'justify-end' : 'justify-start'} opacity-0 transition-opacity group-hover:opacity-100`}
          >
            <button
              onClick={() => onLike(message.id, message.likes || [])}
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors ${
                message.likes?.some(like => like.userId === currentUser?.id)
                  ? isDarkMode
                    ? 'bg-red-900/20 text-red-400'
                    : 'bg-red-50 text-red-600'
                  : isDarkMode
                    ? 'text-gray-400 hover:bg-red-900/20 hover:text-red-400'
                    : 'text-gray-500 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              <Heart
                size={14}
                fill={
                  message.likes?.some(like => like.userId === currentUser?.id)
                    ? 'currentColor'
                    : 'none'
                }
              />
              <span>{message.likes?.length || 0}</span>
            </button>

            <button
              onClick={() => setReplyingTo(replyingTo === message.id ? null : message.id)}
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-blue-900/20 hover:text-blue-400'
                  : 'text-gray-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <Reply size={14} />
              <span>Reply</span>
            </button>

            <button
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-green-900/20 hover:text-green-400'
                  : 'text-gray-500 hover:bg-green-50 hover:text-green-600'
              }`}
            >
              <Share size={14} />
              <span>Share</span>
            </button>

            {canEditDelete && (
              <div className="relative">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setOpenOptionsMenu(openOptionsMenu === message.id ? null : message.id);
                  }}
                  className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <MoreVertical size={14} />
                </button>
                {openOptionsMenu === message.id && (
                  <div
                    className={`absolute bottom-full right-0 z-10 mb-2 rounded-xl border shadow-xl ${
                      isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                    }`}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setEditingMessage(message.id);
                          setEditText(message.text);
                          setOpenOptionsMenu(null);
                        }}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-xs transition-colors ${
                          isDarkMode
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(message.id)}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-xs transition-colors ${
                          isDarkMode
                            ? 'text-red-400 hover:bg-red-900/20'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <AnimatePresence>
            {replyingTo === message.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4"
              >
                <div className="flex gap-3">
                  <input
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    className={`flex-1 rounded-xl px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                        : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
                    }`}
                    onKeyDown={e => {
                      if (e.key === 'Enter') onReply(message.id, message.replies || []);
                      if (e.key === 'Escape') setReplyingTo(null);
                    }}
                    autoFocus
                  />
                  <motion.button
                    onClick={() => onReply(message.id, message.replies || [])}
                    className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Reply
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {message.replies && message.replies.length > 0 && (
            <div className="mt-3 space-y-2">
              {message.replies
                .filter(reply => !reply.parentReplyId)
                .slice(0, expandedReplies[message.id] ? undefined : 3)
                .map(reply => (
                  <ReplyMessage
                    key={reply.id}
                    reply={reply}
                    messageId={message.id}
                    currentUser={currentUser}
                    onUpdateReply={onUpdateReply}
                    onDeleteReply={onDeleteReply}
                    onReplyLike={onReplyLike}
                    onNestedReply={onNestedReply}
                    setEditingReply={setEditingReply}
                    editingReply={editingReply}
                    editReplyText={editReplyText}
                    setEditReplyText={setEditReplyText}
                    nestedReplyTo={nestedReplyTo}
                    setNestedReplyTo={setNestedReplyTo}
                    nestedReplyText={nestedReplyText}
                    setNestedReplyText={setNestedReplyText}
                    messageReplies={message.replies}
                    openReplyOptionsMenu={openReplyOptionsMenu}
                    setOpenReplyOptionsMenu={setOpenReplyOptionsMenu}
                  />
                ))}

              {message.replies.length > 3 && (
                <button
                  onClick={() =>
                    setExpandedReplies(prev => ({
                      ...prev,
                      [message.id]: !prev[message.id],
                    }))
                  }
                  className="text-xs text-blue-600 transition-colors hover:text-blue-700"
                >
                  {expandedReplies[message.id]
                    ? 'Show less'
                    : `Show ${message.replies.length - 3} more replies`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ReplyMessage = ({
  reply,
  messageId,
  currentUser,
  onUpdateReply,
  onDeleteReply,
  onReplyLike,
  onNestedReply,
  setEditingReply,
  editingReply,
  editReplyText,
  setEditReplyText,
  nestedReplyTo,
  setNestedReplyTo,
  nestedReplyText,
  setNestedReplyText,
  messageReplies,
  openReplyOptionsMenu,
  setOpenReplyOptionsMenu,
}) => {
  const isOwnReply = currentUser && currentUser.id === reply.authorId;
  const canEditDelete =
    currentUser &&
    (currentUser.id === reply.authorId ||
      currentUser.role === 'admin' ||
      currentUser.role === 'moderator');

  const getRoleColor = role => {
    const colors = {
      admin: 'bg-red-50 text-black border-gray-900',
      moderator: 'bg-blue-50 text-black border-gray-900',
      alumni: 'bg-green-50 text-black border-gray-900',
      client: 'bg-purple-50 text-black border-gray-900',
      user: 'bg-gray-50 text-black border-gray-900',
    };
    return colors[role] || colors.user;
  };

  const formatTime = timestamp => {
    if (!timestamp?.toDate) return 'now';
    const date = timestamp.toDate();
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return date.toLocaleDateString();
  };

  const nestedReplies = messageReplies.filter(r => r.parentReplyId === reply.id);

  return (
    <div className={`flex ${isOwnReply ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`flex max-w-[95%] sm:max-w-[85%] ${isOwnReply ? 'flex-row-reverse' : 'flex-row'}`}
      >
        <div className={`flex-shrink-0 ${isOwnReply ? 'ml-2' : 'mr-2'}`}>
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-500 text-xs font-medium text-white">
            {reply.authorName
              ?.split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase() || 'U'}
          </div>
        </div>

        <div className={`flex-1 ${isOwnReply ? 'text-right' : 'text-left'}`}>
          <div
            className={`mb-1 flex items-center gap-2 ${isOwnReply ? 'justify-end' : 'justify-start'}`}
          >
            <span className="text-xs font-medium text-black">{reply.authorName}</span>
            <span
              className={`rounded-full border px-1.5 py-0.5 text-xs font-medium ${getRoleColor(reply.authorRole)}`}
            >
              {reply.authorRole}
            </span>
            <span className="text-xs text-black">{formatTime(reply.createdAt)}</span>
            {reply.isEdited && <span className="text-xs text-black">(edited)</span>}
          </div>

          <div
            className={`inline-block rounded-xl px-3 py-2 ${
              isOwnReply
                ? 'rounded-br-sm bg-blue-500 text-white'
                : 'rounded-bl-sm bg-gray-200 text-black'
            }`}
          >
            {editingReply === reply.id ? (
              <div className="flex items-center gap-2">
                <input
                  value={editReplyText}
                  onChange={e => setEditReplyText(e.target.value)}
                  className="flex-1 border-none bg-transparent text-xs text-black outline-none"
                  onKeyDown={e => {
                    if (e.key === 'Enter') onUpdateReply(messageId, reply.id);
                    if (e.key === 'Escape') {
                      setEditingReply(null);
                      setEditReplyText('');
                    }
                  }}
                  autoFocus
                />
                <button
                  onClick={() => onUpdateReply(messageId, reply.id)}
                  className="rounded p-1 hover:bg-white/20"
                >
                  <Check size={12} />
                </button>
                <button
                  onClick={() => {
                    setEditingReply(null);
                    setEditReplyText('');
                  }}
                  className="rounded p-1 hover:bg-white/20"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <p className="text-xs leading-relaxed">{reply.text}</p>
            )}
          </div>

          <div
            className={`mt-1 flex items-center gap-2 ${isOwnReply ? 'justify-end' : 'justify-start'}`}
          >
            <button
              onClick={() => onReplyLike(messageId, reply.id, reply.likes || [])}
              className={`flex items-center gap-1 text-xs transition-colors ${
                reply.likes?.some(like => like.userId === currentUser?.id)
                  ? 'text-red-500'
                  : 'text-gray-400 hover:text-red-500'
              }`}
            >
              <Heart
                size={10}
                fill={
                  reply.likes?.some(like => like.userId === currentUser?.id)
                    ? 'currentColor'
                    : 'none'
                }
              />
              <span>{reply.likes?.length || 0}</span>
            </button>

            <button
              onClick={() => setNestedReplyTo(nestedReplyTo === reply.id ? null : reply.id)}
              className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-blue-500"
            >
              <MessageCircle size={10} />
              <span>Reply</span>
            </button>

            {canEditDelete && (
              <div className="relative">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setOpenReplyOptionsMenu(openReplyOptionsMenu === reply.id ? null : reply.id);
                  }}
                  className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-gray-600"
                >
                  <MoreVertical size={10} />
                </button>
                {openReplyOptionsMenu === reply.id && (
                  <div
                    className="absolute bottom-full right-0 z-10 mb-1 rounded-lg border border-gray-200 bg-white shadow-lg"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setEditingReply(reply.id);
                          setEditReplyText(reply.text);
                          setOpenReplyOptionsMenu(null);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        <Edit2 size={10} />
                        Edit
                      </button>
                      <button
                        onClick={() => onDeleteReply(messageId, reply.id)}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={10} />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Nested Reply Input */}
          <AnimatePresence>
            {nestedReplyTo === reply.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2"
              >
                <div className="flex gap-2">
                  <input
                    value={nestedReplyText}
                    onChange={e => setNestedReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={e => {
                      if (e.key === 'Enter') onNestedReply(messageId, reply.id);
                      if (e.key === 'Escape') setNestedReplyTo(null);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => onNestedReply(messageId, reply.id)}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white transition-colors hover:bg-blue-700"
                  >
                    Reply
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Nested Replies */}
          {nestedReplies.length > 0 && (
            <div className="mt-2 space-y-1 border-l-2 border-gray-200 pl-4">
              {nestedReplies.map(nestedReply => {
                const isOwnNestedReply = currentUser && currentUser.id === nestedReply.authorId;
                return (
                  <div
                    key={nestedReply.id}
                    className={`flex ${isOwnNestedReply ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex max-w-[98%] sm:max-w-[90%] ${isOwnNestedReply ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      <div className={`flex-shrink-0 ${isOwnNestedReply ? 'ml-1' : 'mr-1'}`}>
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-blue-300 to-blue-400 text-xs font-medium text-white">
                          {nestedReply.authorName
                            ?.split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase() || 'U'}
                        </div>
                      </div>
                      <div className={`flex-1 ${isOwnNestedReply ? 'text-right' : 'text-left'}`}>
                        <div
                          className={`mb-1 flex items-center gap-1 ${isOwnNestedReply ? 'justify-end' : 'justify-start'}`}
                        >
                          <span className="text-xs font-medium text-black">
                            {nestedReply.authorName}
                          </span>
                          <span
                            className={`rounded-full border px-1 py-0.5 text-xs font-medium ${getRoleColor(nestedReply.authorRole)}`}
                          >
                            {nestedReply.authorRole}
                          </span>
                          <span className="text-xs text-black">
                            {formatTime(nestedReply.createdAt)}
                          </span>
                        </div>
                        <div
                          className={`inline-block rounded-lg px-2 py-1.5 ${
                            isOwnNestedReply
                              ? 'rounded-br-sm bg-blue-400 text-white'
                              : 'rounded-bl-sm bg-gray-300 text-black'
                          }`}
                        >
                          <p className="text-xs">{nestedReply.text}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function PublicChat() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [expandedReplies, setExpandedReplies] = useState({});
  const [nestedReplyTo, setNestedReplyTo] = useState(null);
  const [nestedReplyText, setNestedReplyText] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPinnedMessages, setShowPinnedMessages] = useState(true);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState('general');
  const [channelCounts, setChannelCounts] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [openOptionsMenu, setOpenOptionsMenu] = useState(null); // 'message-id' or 'reply-id'
  const [openReplyOptionsMenu, setOpenReplyOptionsMenu] = useState(null); // reply id
  const [channels] = useState([
    { id: 'general', name: 'General', icon: Hash },
    { id: 'announcements', name: 'Announcements', icon: Megaphone },
    { id: 'help', name: 'Help & Support', icon: HelpCircle },
    { id: 'showcase', name: 'Project Showcase', icon: Star },
    { id: 'networking', name: 'Networking', icon: Users },
  ]);

  const currentUser = useCurrentUser();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch all messages to count per channel
  useEffect(() => {
    if (!currentUser) return;

    const messagesRef = collection(firestore, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, snapshot => {
      const allMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Count messages per channel
      const counts = {};
      channels.forEach(channel => {
        const count = allMessages.filter(msg => (msg.channel || 'general') === channel.id).length;
        counts[channel.id] = count;
      });
      setChannelCounts(counts);
    });

    return () => unsubscribe();
  }, [currentUser, channels]);

  // Fetch messages for selected channel
  useEffect(() => {
    if (!currentUser) return;

    const messagesRef = collection(firestore, 'messages');
    const q = query(
      messagesRef,
      where('channel', '==', selectedChannel),
      orderBy('createdAt', 'desc')
    );

    let unsubscribe = onSnapshot(
      q,
      snapshot => {
        const messagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(messagesData);
        setLoading(false);
        setTimeout(scrollToBottom, 100);
      },
      error => {
        // If index doesn't exist, fetch without where and filter client-side

        const messagesRef = collection(firestore, 'messages');
        const q2 = query(messagesRef, orderBy('createdAt', 'desc'));
        unsubscribe = onSnapshot(q2, snapshot => {
          const allMessages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          const filteredMessages = allMessages.filter(
            msg => (msg.channel || 'general') === selectedChannel
          );
          setMessages(filteredMessages);
          setLoading(false);
          setTimeout(scrollToBottom, 100);
        });
      }
    );

    return () => unsubscribe();
  }, [currentUser, selectedChannel]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileSelect = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file sizes (10MB max per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = index => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    // Allow sending if: text only, text + files, or files only
    const hasText = newMessage.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;

    // Early return if nothing to send or no user
    if ((!hasText && !hasFiles) || !currentUser) {
      return;
    }

    try {
      let uploadedFiles = [];

      // Upload files first if any
      if (hasFiles) {
        setUploadingFiles(selectedFiles.map((_, i) => i));

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          try {
            const timestamp = Date.now();
            const storagePath = `messages/${selectedChannel}/${timestamp}_${file.name}`;
            const storageRef = ref(storage, storagePath);

            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            uploadedFiles.push({
              name: file.name,
              url: downloadURL,
              type: file.type,
              size: file.size,
            });
          } catch (error) {
            toast.error(`Error uploading ${file.name}: ${error.message}`);
          }
        }

        setUploadingFiles([]);
      }

      if (hasText || uploadedFiles.length > 0) {
        const messageData = {
          text: hasText ? newMessage.trim() : '',
          authorId: currentUser.id,
          authorName: `${currentUser.firstname} ${currentUser.lastname}`,
          authorEmail: currentUser.email,
          authorRole: currentUser.role,
          channel: selectedChannel,
          createdAt: new Date(),
          updatedAt: new Date(),
          likes: [],
          replies: [],
        };

        // Only add files field if there are files (Firestore doesn't allow undefined)
        if (uploadedFiles.length > 0) {
          messageData.files = uploadedFiles;
        }

        await addDoc(collection(firestore, 'messages'), messageData);

        // Clear inputs
        setNewMessage('');
        setSelectedFiles([]);

        // Show success message
        if (hasText && uploadedFiles.length > 0) {
          toast.success('Message and files sent successfully');
        } else if (hasText) {
          toast.success('Message sent successfully');
        } else if (uploadedFiles.length > 0) {
          toast.success('Files sent successfully');
        }
      } else {
        // Files were selected but all failed to upload and no text
        toast.error('Failed to upload files. Please try again.');
      }
    } catch (error) {
      toast.error('Error sending message: ' + error.message);
      setUploadingFiles([]);
    }
  };

  const handleUpdate = async messageId => {
    if (!editText.trim()) return;

    try {
      await updateDoc(doc(firestore, 'messages', messageId), {
        text: editText.trim(),
        updatedAt: new Date(),
        isEdited: true,
      });
      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      toast.error('Error updating message: ' + error.message);
    }
  };

  const handleDelete = async (messageId, confirmed = false) => {
    if (!confirmed) {
      setShowDeleteConfirm({ type: 'message', id: messageId });
      setOpenOptionsMenu(null);
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'messages', messageId));
      setShowDeleteConfirm(null);
      toast.success('Message deleted successfully');
    } catch (error) {
      toast.error('Error deleting message: ' + error.message);
      setShowDeleteConfirm(null);
    }
  };

  const handleLike = async (messageId, currentLikes) => {
    if (!currentUser) return;

    const userLiked = currentLikes.some(like => like.userId === currentUser.id);
    let newLikes;

    if (userLiked) {
      newLikes = currentLikes.filter(like => like.userId !== currentUser.id);
    } else {
      newLikes = [
        ...currentLikes,
        {
          userId: currentUser.id,
          userName: `${currentUser.firstname} ${currentUser.lastname}`,
          timestamp: new Date(),
        },
      ];
    }

    try {
      await updateDoc(doc(firestore, 'messages', messageId), {
        likes: newLikes,
      });
    } catch (error) {
      toast.error('Error updating likes: ' + error.message);
    }
  };

  const handleReply = async (messageId, currentReplies) => {
    if (!replyText.trim() || !currentUser) return;

    const newReply = {
      id: Date.now().toString(),
      text: replyText.trim(),
      authorId: currentUser.id,
      authorName: `${currentUser.firstname} ${currentUser.lastname}`,
      authorRole: currentUser.role,
      createdAt: new Date(),
    };

    const updatedReplies = [...currentReplies, newReply];

    try {
      await updateDoc(doc(firestore, 'messages', messageId), {
        replies: updatedReplies,
      });
      setReplyingTo(null);
      setReplyText('');
    } catch (error) {
      toast.error('Error adding reply: ' + error.message);
    }
  };

  const handleUpdateReply = async (messageId, replyId) => {
    if (!editReplyText.trim()) return;

    try {
      const messageRef = doc(firestore, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      const message = messageDoc.data();

      const updatedReplies = message.replies.map(reply =>
        reply.id === replyId
          ? { ...reply, text: editReplyText.trim(), isEdited: true, updatedAt: new Date() }
          : reply
      );

      await updateDoc(messageRef, { replies: updatedReplies });
      setEditingReply(null);
      setEditReplyText('');
    } catch (error) {
      toast.error('Error updating reply: ' + error.message);
    }
  };

  const handleDeleteReply = async (messageId, replyId, confirmed = false) => {
    if (!confirmed) {
      setShowDeleteConfirm({ type: 'reply', messageId, replyId });
      setOpenReplyOptionsMenu(null);
      return;
    }

    try {
      const messageRef = doc(firestore, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      const message = messageDoc.data();

      const updatedReplies = message.replies.filter(reply => reply.id !== replyId);
      await updateDoc(messageRef, { replies: updatedReplies });
      setShowDeleteConfirm(null);
      toast.success('Reply deleted successfully');
    } catch (error) {
      toast.error('Error deleting reply: ' + error.message);
      setShowDeleteConfirm(null);
    }
  };

  const handleReplyLike = async (messageId, replyId, currentLikes = []) => {
    if (!currentUser) return;

    const userLiked = currentLikes.some(like => like.userId === currentUser.id);
    let newLikes;

    if (userLiked) {
      newLikes = currentLikes.filter(like => like.userId !== currentUser.id);
    } else {
      newLikes = [
        ...currentLikes,
        {
          userId: currentUser.id,
          userName: `${currentUser.firstname} ${currentUser.lastname}`,
          timestamp: new Date(),
        },
      ];
    }

    try {
      const messageRef = doc(firestore, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      const message = messageDoc.data();

      const updatedReplies = message.replies.map(reply =>
        reply.id === replyId ? { ...reply, likes: newLikes } : reply
      );

      await updateDoc(messageRef, { replies: updatedReplies });
    } catch (error) {
      toast.error('Error updating reply likes: ' + error.message);
    }
  };

  const handleNestedReply = async (messageId, parentReplyId) => {
    if (!nestedReplyText.trim() || !currentUser) return;

    const newReply = {
      id: Date.now().toString(),
      text: nestedReplyText.trim(),
      authorId: currentUser.id,
      authorName: `${currentUser.firstname} ${currentUser.lastname}`,
      authorRole: currentUser.role,
      createdAt: new Date(),
      parentReplyId,
      likes: [],
      replies: [],
    };

    try {
      const messageRef = doc(firestore, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      const message = messageDoc.data();

      const updatedReplies = [...message.replies, newReply];
      await updateDoc(messageRef, { replies: updatedReplies });
      setNestedReplyTo(null);
      setNestedReplyText('');
    } catch (error) {
      toast.error('Error adding nested reply: ' + error.message);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenOptionsMenu(null);
      setOpenReplyOptionsMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div
        className={`flex h-96 items-center justify-center ${
          isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
        }`}
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-[90vh] w-full overflow-hidden rounded-xl shadow-2xl ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      {/* Sidebar */}
      <div
        className={`w-80 flex-shrink-0 border-r ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        } ${!showSidebar ? 'hidden md:block' : ''}`}
      >
        {/* Sidebar Header */}
        <div className={`border-b p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Community
            </h2>
          </div>

          <div className="relative">
            <Search
              className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={`w-full rounded-xl py-3 pl-10 pr-4 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
        </div>

        {/* Channels List */}
        <div className="p-4">
          <h3
            className={`mb-3 text-sm font-semibold ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            CHANNELS
          </h3>
          <div className="space-y-1">
            {channels.map(channel => {
              const Icon = channel.icon;
              return (
                <motion.button
                  key={channel.id}
                  onClick={() => setSelectedChannel(channel.id)}
                  className={`flex w-full items-center justify-between rounded-xl p-3 transition-all ${
                    selectedChannel === channel.id
                      ? isDarkMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-700'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} />
                    <span className="font-medium">{channel.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {channelCounts[channel.id] > 0 && (
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-bold ${
                          selectedChannel === channel.id
                            ? 'bg-white text-blue-600'
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        {channelCounts[channel.id] > 100 ? '99+' : channelCounts[channel.id]}
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Members Count */}
        <div className={`border-t p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="mb-3 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500"></div>
            <span
              className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
            >
              TOTAL MEMBERS
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {totalMembers}
            </span>
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              members
            </span>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col">
        {/* Chat Header */}
        <div
          className={`flex items-center justify-between border-b px-6 py-4 ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="rounded-lg p-2 hover:bg-gray-100 md:hidden"
            >
              <Hash size={20} />
            </button>
            <div>
              <h3
                className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
              >
                #{selectedChannel}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {channelCounts[selectedChannel] || 0}{' '}
                {channelCounts[selectedChannel] === 1 ? 'message' : 'messages'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`rounded-lg p-2 transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <Bell size={20} />
            </button>
            <button
              className={`rounded-lg p-2 transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <Pin size={20} />
            </button>
            <button
              className={`rounded-lg p-2 transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <Users size={20} />
            </button>
          </div>
        </div>

        {/* Pinned Messages */}
        {showPinnedMessages && pinnedMessages.length > 0 && (
          <div
            className={`border-b px-6 py-3 ${
              isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-yellow-50'
            }`}
          >
            <div className="mb-2 flex items-center gap-2">
              <Pin size={16} className="text-yellow-500" />
              <span
                className={`text-sm font-semibold ${
                  isDarkMode ? 'text-yellow-400' : 'text-yellow-700'
                }`}
              >
                Pinned Messages
              </span>
              <button
                onClick={() => setShowPinnedMessages(false)}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2">
              {pinnedMessages.slice(0, 2).map(message => (
                <div
                  key={message.id}
                  className={`rounded-lg p-2 text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <span className="font-medium">{message.authorName}: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    {message.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Container */}
        <div
          className={`flex-1 overflow-y-auto px-6 py-4 ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`}
        >
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {messages
                .filter(message => {
                  if (!searchTerm.trim()) return true;
                  const searchLower = searchTerm.toLowerCase();
                  return (
                    message.text?.toLowerCase().includes(searchLower) ||
                    message.authorName?.toLowerCase().includes(searchLower)
                  );
                })
                .map(message => (
                  <Message
                    key={message.id}
                    message={message}
                    currentUser={currentUser}
                    onLike={handleLike}
                    onReply={handleReply}
                    onEdit={handleUpdate}
                    onDelete={handleDelete}
                    onUpdateReply={handleUpdateReply}
                    onDeleteReply={handleDeleteReply}
                    onReplyLike={handleReplyLike}
                    onNestedReply={handleNestedReply}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyText={replyText}
                    setReplyText={setReplyText}
                    editingMessage={editingMessage}
                    setEditingMessage={setEditingMessage}
                    editText={editText}
                    setEditText={setEditText}
                    editingReply={editingReply}
                    setEditingReply={setEditingReply}
                    editReplyText={editReplyText}
                    setEditReplyText={setEditReplyText}
                    nestedReplyTo={nestedReplyTo}
                    setNestedReplyTo={setNestedReplyTo}
                    nestedReplyText={nestedReplyText}
                    setNestedReplyText={setNestedReplyText}
                    expandedReplies={expandedReplies}
                    setExpandedReplies={setExpandedReplies}
                    isDarkMode={isDarkMode}
                    openOptionsMenu={openOptionsMenu}
                    setOpenOptionsMenu={setOpenOptionsMenu}
                    openReplyOptionsMenu={openReplyOptionsMenu}
                    setOpenReplyOptionsMenu={setOpenReplyOptionsMenu}
                  />
                ))}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {messages.filter(message => {
            if (!searchTerm.trim()) return true;
            const searchLower = searchTerm.toLowerCase();
            return (
              message.text?.toLowerCase().includes(searchLower) ||
              message.authorName?.toLowerCase().includes(searchLower)
            );
          }).length === 0 && (
            <div className="py-12 text-center">
              <MessageCircle
                size={48}
                className={`mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}
              />
              <p
                className={`mb-2 text-lg font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                Welcome to #{selectedChannel}!
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This is the beginning of your conversation. Start by sharing your thoughts!
              </p>
            </div>
          )}
        </div>

        {/* Message Input */}
        <div
          className={`border-t px-6 py-4 ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}
        >
          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}
                >
                  {file.type?.startsWith('image/') ? (
                    <Image size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                  ) : file.type?.startsWith('video/') ? (
                    <Video size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                  ) : (
                    <File size={16} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
                  )}
                  <span
                    className={`max-w-[150px] truncate text-xs ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeFile(index)}
                    className={`rounded p-0.5 transition-colors ${
                      isDarkMode
                        ? 'text-gray-400 hover:bg-gray-600 hover:text-gray-200'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                    }`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Uploading Files Indicator */}
          {uploadingFiles.length > 0 && (
            <div className="mb-3 flex items-center gap-2 text-sm text-blue-600">
              <Loader2 size={16} className="animate-spin" />
              <span>Uploading {uploadingFiles.length} file(s)...</span>
            </div>
          )}

          <div className="flex items-end gap-3">
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
            />
            <label
              htmlFor="file-upload"
              className={`cursor-pointer rounded-full p-2 transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <Paperclip size={20} />
            </label>
            <div className="relative flex-1">
              <textarea
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={`Message #${selectedChannel}`}
                rows={1}
                className={`w-full resize-none rounded-2xl px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                    : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
                }`}
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <motion.button
              onClick={handleSend}
              disabled={
                (!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles.length > 0
              }
              className="flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {uploadingFiles.length > 0 ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6 shadow-2xl ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="mb-4">
                <h3
                  className={`mb-2 text-lg font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Confirm Delete
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Are you sure you want to delete this{' '}
                  {showDeleteConfirm.type === 'message' ? 'message' : 'reply'}? This action cannot
                  be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (showDeleteConfirm.type === 'message') {
                      handleDelete(showDeleteConfirm.id, true);
                    } else {
                      handleDeleteReply(
                        showDeleteConfirm.messageId,
                        showDeleteConfirm.replyId,
                        true
                      );
                    }
                  }}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default PublicChat;
